import React from 'react';
import AmountInput from '../ui/AmountInput';
import './FuelsFundingPanel.css';

/**
 * Bunkers FUNDING section (Phase 1, May 2026).
 *
 * Renders on PURCHASE recaps only. Three trader-authored inputs
 * (Funding Days, Funding Amount, Interest Rate) drive two derived
 * KPIs (Interest Expense, Net Profit After Interest).
 *
 * Funding Days is editable via ``funding_days_override``. When the
 * trader hasn't entered an override the input shows the derived
 * ``effective_funding_days`` (= customer_credit_days -
 * supplier_credit_days, snapshotted at conversion) as a sensible
 * default. Clearing the input reverts to the derived value.
 *
 * Props:
 *   recapType:                 'SALE' | 'PURCHASE' (gate; PURCHASE only)
 *   commodityType:             'fuels' | 'solids'  (gate; fuels only)
 *   effective_funding_days:    number | null  (derived/override value
 *                              from the server -- used as the initial
 *                              display when funding_days_override is null)
 *   funding_days_override:     string | number | null  (editable; trader
 *                              override stored on the recap)
 *   funding_amount:            string | number | null  (editable)
 *   interest_rate_pct:         string | number | null  (editable)
 *   net_profit:                string | number | null  (read-only @property
 *                              from the Bunkers Financial Summary --
 *                              needed for the live net_profit_after_interest
 *                              preview)
 *   onChange:                  (field, value) => void   FE field change
 *                              callback; expected to set ffp[field] +
 *                              flip the hasUnsavedChanges flag.
 *   disabled?:                 boolean                  Disable inputs
 *                              when the recap is locked / read-only.
 *
 * Interest Expense + Net Profit After Interest are computed LIVE on
 * the FE using the same formula as the backend's @property's so the
 * trader sees the calc update on every keystroke. The backend still
 * recomputes + persists the canonical values on save; the FE preview
 * just removes the round-trip latency.
 */
const formatUSD = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    return `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

// Coerce a form-field value (which can be '' / null / number / number-as-string)
// to a finite Number, or null if it's missing/blank/non-numeric. Used so the
// live preview matches the backend's "any input missing -> None" contract.
const toNumOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const FuelsFundingPanel = ({
    recapType,
    commodityType,
    effective_funding_days,
    funding_days_override,
    funding_amount,
    interest_rate_pct,
    net_profit,
    onChange,
    disabled = false,
}) => {
    if (commodityType !== 'fuels') return null;
    if (recapType !== 'PURCHASE') return null;

    const handle = (field) => (v) => {
        // Allow empty string so the trader can clear the field; the
        // backend column is nullable. Otherwise pass the canonical
        // dot-decimal string through -- DRF DecimalField parses it.
        // AmountInput already guards keystrokes and emits '' when cleared.
        onChange(field, v === '' ? null : v);
    };

    // Display priority for the Funding Days input: trader override if
    // they have one queued/saved, else the server-derived effective
    // value (= customer_credit_days - supplier_credit_days). Clearing
    // the field pushes null back to the backend and reverts the calcs
    // to the derived value on next save.
    const fundingDaysDisplay = funding_days_override !== null
        && funding_days_override !== undefined
        && funding_days_override !== ''
        ? funding_days_override
        : (effective_funding_days ?? '');

    // Live calc -- mirrors backend's TradeRecap.interest_expense /
    // .net_profit_after_interest @property's so the trader sees the
    // result update on every keystroke instead of waiting for a save
    // round-trip. The backend remains the source of truth on save.
    const fundingDaysNum = toNumOrNull(fundingDaysDisplay);
    const fundingAmountNum = toNumOrNull(funding_amount);
    const interestRateNum = toNumOrNull(interest_rate_pct);
    const netProfitNum = toNumOrNull(net_profit);

    const liveInterestExpense = (
        fundingDaysNum !== null
        && fundingAmountNum !== null
        && interestRateNum !== null
    )
        ? fundingAmountNum * (interestRateNum / 100) * (fundingDaysNum / 365)
        : null;

    const liveNetProfitAfterInterest = (
        liveInterestExpense !== null && netProfitNum !== null
    )
        ? netProfitNum - liveInterestExpense
        : null;

    const isNegativeIE = liveInterestExpense !== null && liveInterestExpense < 0;
    const isNegativeNPAI = liveNetProfitAfterInterest !== null && liveNetProfitAfterInterest < 0;

    return (
        <div className="ffp-grid">
            {/* Top row: Funding Days + Funding Amount + Interest Rate (all editable) */}
            <div className="ffp-cell">
                <label className="ffp-label" htmlFor="ffp-funding-days">
                    Funding Days
                </label>
                <AmountInput
                    id="ffp-funding-days"
                    className="ffp-input"
                    kind="integer"
                    allowNegative
                    placeholder="e.g. 46"
                    value={fundingDaysDisplay}
                    onChange={handle('funding_days_override')}
                    disabled={disabled}
                />
            </div>
            <div className="ffp-cell">
                <label className="ffp-label" htmlFor="ffp-funding-amount">
                    Funding Amount
                </label>
                <AmountInput
                    id="ffp-funding-amount"
                    className="ffp-input"
                    placeholder="e.g. 50000"
                    value={funding_amount ?? ''}
                    onChange={handle('funding_amount')}
                    disabled={disabled}
                />
            </div>
            <div className="ffp-cell">
                <label className="ffp-label" htmlFor="ffp-interest-rate">
                    Interest Rate
                </label>
                <div className="ffp-input-with-suffix">
                    <AmountInput
                        id="ffp-interest-rate"
                        className="ffp-input ffp-input--pct"
                        placeholder="e.g. 5"
                        value={interest_rate_pct ?? ''}
                        onChange={handle('interest_rate_pct')}
                        disabled={disabled}
                    />
                    <span className="ffp-input-suffix">%</span>
                </div>
            </div>

            {/* Bottom row: Interest Expense + Net Profit After Interest
                (both live FE previews -- the backend recomputes on save). */}
            <div className="ffp-cell ffp-cell--span-2">
                <span className="ffp-label">Interest Expense</span>
                <span
                    className={[
                        'ffp-value', 'ffp-value--readonly',
                        isNegativeIE ? 'ffp-value--neg' : '',
                    ].filter(Boolean).join(' ')}
                >
                    {formatUSD(liveInterestExpense)}
                </span>
            </div>
            <div className="ffp-cell">
                <span className="ffp-label">Net Profit After Interest</span>
                <span
                    className={[
                        'ffp-value', 'ffp-value--readonly', 'ffp-value--strong',
                        isNegativeNPAI ? 'ffp-value--neg' : '',
                    ].filter(Boolean).join(' ')}
                >
                    {formatUSD(liveNetProfitAfterInterest)}
                </span>
            </div>
        </div>
    );
};

export default FuelsFundingPanel;
